import hash from 'object-hash';
import get from 'lodash/get';
import omit from 'lodash/omit';
import assignIn from 'lodash/assignIn';
import Auth from '@Services/Auth';
import PendoService from '@Services/Pendo';
import AppStorage from '@Services/AppStorage';
import { PENDO_URL } from '@Constants/BuildInfo';
import Login from '@Services/Login';
import Register from '@Services/Register';
import Catalyst from '@Services/Catalyst';
import { doesUsernameMatchBulkId } from '@Pages/Login/BulkIdRegisterModal/utils';
import { bulkIdSubmit } from '@Pages/Login/BulkIdRegisterModal/actions';
import { errorMessage as bulkIdErrorMessage } from '@Pages/Login/BulkIdRegister/BulkIdRegisterConstants';
import { loginTypes } from '@Constants/CommonConstants';
import { closeDialog, updateDialog } from '@Containers/Dialogs/DialogsActions';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { openResetPasswordWithTokenDialog as openResetPasswordWithTokenDialogAction } from './ResetPasswordWithTokenDialog/ResetPasswordWithTokenDialogActions';
import { openLoginTooManySessionsModal as openLoginTooManySessionsModalAction } from './TooManySessionsModal/TooManySessionsModalActions';
import { openForgotPasswordDialog as openForgotPasswordDialogAction } from './ForgotPasswordDialog/ForgotPasswordDialogActions';

import {
  loginSetHasInstitutionLoginReducerType,
  loginSetHasRegistrationReducerType,
  loginSetIpAddressReducerType,
  loginSetIsFormDisabledReducerType,
  loginSetIsLoadingReducerType,
  loginSetLoginFailReducerType,
  loginSetNotificationsReducerType,
  loginSetOccurrenceDateReducerType,
  loginSetTimestampReducerType,
  loginSetDataReducerType,
  typeOfLoginModal,
} from './LoginConstants';
import { initialState } from './LoginReducer';

export function closeLoginModal() {
  return (dispatch) => {
    dispatch(
      closeDialog({
        id: typeOfLoginModal,
      })
    );
  };
}

export const cancel = () => async (dispatch) => {
  await Login.cancelLogin();
  dispatch(closeLoginModal());
};

export const openLoginTooManySessionsModal = () => async (dispatch) => {
  dispatch(openLoginTooManySessionsModalAction());
};

export const openResetPasswordWithTokenDialog = (token) => async (dispatch) => {
  try {
    await MessageBroker.send(RequestUrls.resetPasswordValidate, { token });
    dispatch(openResetPasswordWithTokenDialogAction({ token }));
  } catch (error) {
    if (error?.status === 404) {
      NotifyDialogsService.openModalWindow({
        title: 'Error',
        text: 'The password reset link has expired. Please try resetting your password again.',
        type: 'error',
        confirmText: 'Close',
        trackingOptions: {
          component: 'Reset password',
        },
      });
    } else {
      Logger.error(error);
    }
  }
};

export const openForgotPasswordDialog = () => async (dispatch) => {
  dispatch(closeLoginModal());
  dispatch(openForgotPasswordDialogAction());
};

export function setIpAddress(ipAddress) {
  return {
    type: loginSetIpAddressReducerType,
    payload: { ipAddress },
  };
}

export function setLoginFail(loginFail) {
  return {
    type: loginSetLoginFailReducerType,
    payload: { loginFail },
  };
}

export function setIsFormDisabled(isFormDisabled) {
  return {
    type: loginSetIsFormDisabledReducerType,
    payload: { isFormDisabled },
  };
}

export function setIsLoading(isLoading) {
  return {
    type: loginSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setHasRegistration(hasRegistration) {
  return {
    type: loginSetHasRegistrationReducerType,
    payload: { hasRegistration },
  };
}

export function setHasInstitutionLogin(hasInstitutionLogin) {
  return {
    type: loginSetHasInstitutionLoginReducerType,
    payload: { hasInstitutionLogin },
  };
}

export function setNotifications(notifications) {
  return {
    type: loginSetNotificationsReducerType,
    payload: { notifications },
  };
}

export function setOccurrenceDate(occurrenceDate) {
  return {
    type: loginSetOccurrenceDateReducerType,
    payload: { occurrenceDate },
  };
}

export function setTimestamp(timestamp) {
  return {
    type: loginSetTimestampReducerType,
    payload: { timestamp },
  };
}

export function setData(data) {
  return {
    type: loginSetDataReducerType,
    payload: data,
  };
}

export const init = () => async (dispatch) => {
  dispatch(setLoginFail(null));

  const hasInstitutionLogin = Auth.hasInstitutionLogin();
  dispatch(setHasInstitutionLogin(hasInstitutionLogin));

  const hasRegistration = Auth.isRegistrationEnabled();
  dispatch(setHasRegistration(hasRegistration));

  const notifications = Auth.getUserNotifications();
  dispatch(setNotifications(notifications));

  Catalyst.trackSimpleEvent('loginStart');

  await PendoService.link(PENDO_URL)
    .then(() => PendoService.updateVisitor())
    .catch(Logger.warn);

  await Auth.getClientInfo()
    .then((data) => {
      dispatch(setIpAddress(data.ipAddress));
    })
    .catch(Logger.error);
};

const login = (data) => async (dispatch) => {
  const bulk = Register.getData();
  let grantType;
  let updatedData = data;
  if (bulk.bulkId && bulk.bulkPassword) {
    grantType = { grant_type: 'bulk' };
    updatedData = assignIn(data, { bulkId: bulk.bulkId });
  }
  Logger.debug('Login data:', { ...updatedData, grantType });
  try {
    await Auth.login(updatedData, undefined, grantType);
    Login.afterAuthRedirect();
    return true;
  } catch (error) {
    // CHOICE type means the user must select an organisation — not a real login failure
    if (error?.type !== 'CHOICE') {
      // login failed
      const errorMessage = error.message || 'Incorrect username or password, please try again';
      Catalyst.trackError(
        {
          message: errorMessage,
          component: 'Login',
        },
        'fe-'
      );
      dispatch(setLoginFail(errorMessage));

      const timestamp = get(error, 'timestamp');
      const occurrenceDate = get(error, 'occurrenceDate');
      if (occurrenceDate) {
        dispatch(setOccurrenceDate(occurrenceDate));
        dispatch(setTimestamp(timestamp));
      }
    }
  }
  return false;
};

const LOGIN_CREDENTIAL_KEY = 'LOGIN_CREDENTIAL_KEY';
let updateCachedCredentialDataTimeout;

const updateCachedCredentialData = (hashedData) => {
  const cachedCredentialData = AppStorage.instance.get(LOGIN_CREDENTIAL_KEY);
  AppStorage.instance.set(LOGIN_CREDENTIAL_KEY, omit(cachedCredentialData, [hashedData]));
};

const startUpdateCachedCredentialDataTimeout = (hashedData) => {
  updateCachedCredentialDataTimeout = setTimeout(() => {
    updateCachedCredentialData(hashedData);
  }, 30000);
};

export const loginSubmit = (data) => async (dispatch) => {
  dispatch(setOccurrenceDate(null));
  dispatch(setTimestamp(null));
  dispatch(setLoginFail(null));
  dispatch(setIsFormDisabled(true));
  dispatch(setIsLoading(true));
  AppStorage.instance.set('loginType', loginTypes.SIMPLE);
  const cachedCredentialData = AppStorage.instance.get(LOGIN_CREDENTIAL_KEY);
  const hashedData = hash({ userName: data.username, password: data.password });

  if (cachedCredentialData?.[hashedData]) {
    dispatch(openLoginTooManySessionsModal());
    dispatch(setIsLoading(false));
    dispatch(setIsFormDisabled(false));
    startUpdateCachedCredentialDataTimeout(hashedData);
    return false;
  }
  startUpdateCachedCredentialDataTimeout(hashedData);

  AppStorage.instance.set(LOGIN_CREDENTIAL_KEY, { ...cachedCredentialData, [hashedData]: true });

  return dispatch(login({ ...data, credentialKey: hashedData })).finally(() => {
    updateCachedCredentialData(hashedData);
    clearTimeout(updateCachedCredentialDataTimeout);
    dispatch(setIsLoading(false));
    dispatch(setIsFormDisabled(false));
  });
};

export const submit = (data) => async (dispatch) => {
  if (doesUsernameMatchBulkId(data.username)) {
    // Bulk ID
    try {
      dispatch(setLoginFail(null));
      dispatch(setIsFormDisabled(true));
      dispatch(setIsLoading(true));

      await dispatch(bulkIdSubmit({ bulkId: data.username, bulkPassword: data.password }));
    } catch (error) {
      Catalyst.trackError(
        {
          message: (error && error.message) || bulkIdErrorMessage,
          component: 'Login',
        },
        'fe-'
      );
      dispatch(setLoginFail(bulkIdErrorMessage));
    }
    dispatch(setIsLoading(false));
    dispatch(setIsFormDisabled(false));
    return false;
  }
  // Login
  return dispatch(loginSubmit(data));
};

export const submitLoginModal = (data) => async (dispatch) => {
  const isLoginSuccessful = await dispatch(submit(data));

  if ((!Auth.isAnonymousUser() && Auth.isReaxysAllowed()) || isLoginSuccessful) {
    await Auth.getUserInfo()
      .then((userData) => Auth.changeUserProfile(true, userData))
      .catch(Logger.error);

    dispatch(closeLoginModal());
  }
};

export const resetState = () => (dispatch) => {
  dispatch(setData(initialState.get('data')));
};

export function openLoginModal() {
  return (dispatch) => {
    dispatch(
      updateDialog({
        id: typeOfLoginModal,
        dialogType: typeOfLoginModal,
        isOpen: true,
      })
    );
  };
}
